import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styles from '../program-management/ProgramManagement.module.css';
import { useSelector, useDispatch } from 'react-redux';
import { setModality, removeModality } from './PatientModalitiesSlice';
import { setDevice, removeDevice, setFormValue, setHubSerialNumber, setSerialNumber } from './PatientEnrollmentSlice';
import ManagementDropdownCard from '../../components/ManagementDropdownCard';
import MultiSelectDropdown from '../../components/MultiSelectDropdown';
import { Tooltip, OverlayTrigger, Form } from "react-bootstrap";
import inputStyles from '../../views/program-management/ProgramManagement.module.css';
import { getInitialStartTime, initSelectedDevices } from '../../utils/programUtils';
import Logger from '../../services/Logger';
import { LogElements, LogAction } from '../../services/LogElements';

const DeviceEnrollment = ({ errors }) => {
    const dispatch = useDispatch();
    const { reenrolling, viewing, enrollment } = useSelector((state) => state.enrollPatientProgram);
    const allDevices = useSelector((state) => state.enrollmentLists.devices); // all available devices
    const filteredDevices = useMemo(() => {
        const inPatientGroups = allDevices.filter((device) =>
            enrollment.groups.some((group) => group.groupId === device.groupId)
        );
        const seenDeviceIds = new Set();
        return inPatientGroups.filter((device) => {
            if (seenDeviceIds.has(device.deviceId)) return false;
            seenDeviceIds.add(device.deviceId);
            return true;
        });
    }, [allDevices, enrollment.groups]);
    const deviceNotes = useSelector((state) => state.enrollPatientProgram.deviceNotes);
    const enrollmentDevices = useSelector((state) => state.enrollPatientProgram.devices); // all devices in program/enrollment
    const [count, setCount] = useState(0);
    const patientId = useSelector((state) => state.enrollPatientProgram.patient.id);
    const savedModalityValues = useSelector((state) => state.savedPatientModalities.patientRecentModalities[patientId]) ?? {};
    const savedSerialNumbers = useSelector((state) => state.savedPatientModalities.patientRecentSerialNumbers[patientId]) ?? {};
    const programModalities = useSelector((state)=>state.patientEnrollmentModalities.modalities);
    const hubs = useSelector(state => state.enrollPatientProgram.hubs);
    const [localHubSerialNumber, setLocalHubSerialNumber] = useState('');
    const [showHubSerialInput, setShowHubSerialInput] = useState(false);
    const [selectedDevices, setSelectedDevices] = useState(initSelectedDevices(enrollmentDevices, allDevices));

    const deviceInfo = {
        deviceId: '',
        serialNumber: '',
        serialNumberId: 0,
        deviceModalityId: 0,
        lowerLimit: 0,
        upperLimit: 0,
        targetValue: 0,
        isAlarmActive: false,
        startTime: getInitialStartTime(),
        frequency: 1,
        interval: "Day",
        readingType: "Automated",
        isContinuous: false
    };

    const modalityInfo = {
        deviceId: '',
        deviceModalityId: '',
        modalityId: '',
        modalityName: '',
        modalityType: '',
        modalityTypeDisplayName: '',
        upperLimit: 0,
        lowerLimit: 0,
        targetValue: 0,
        isAlarmActive: false,
        isContinuous: false,
        startTime: getInitialStartTime(),
        frequency: 1,
        interval: "Day"
    };

    const checkSelectedItem = (deviceId) => {
        return selectedDevices.some(item => item.deviceId === deviceId);
    };

    const handleDeviceChange = (device) => {
        // Log device selection with selected device name as custom message
        Logger.shared.info(
            LogElements.devicesDropdown,
            LogAction.clicked,
            'DeviceEnrollment.js',
            device.listLabel
        );
        
        const deviceInList = selectedDevices.some(item => item.deviceId === device.deviceId);
        if (deviceInList) {
            setSelectedDevices(selectedDevices.filter(item => item.deviceId !== device.deviceId));
            dispatch(removeDevice(device.deviceId));
            dispatch(removeModality(device.deviceId));
        } else {
            setSelectedDevices([device, ...selectedDevices]);
            let tempDevice = { ...deviceInfo, deviceId: device.deviceId };
            if (savedSerialNumbers.hasOwnProperty(device.deviceId)) {
                const saved = savedSerialNumbers[device.deviceId];
                const patientGroupIds = new Set(enrollment.groups.map((g) => g.groupId));
                const serialBelongsToPatientGroup = allDevices.some(
                    (d) =>
                        d.deviceId === device.deviceId &&
                        patientGroupIds.has(d.groupId) &&
                        Array.isArray(d.serialNumbers) &&
                        d.serialNumbers.some((sn) => sn?.id === saved.serialNumberId)
                );

                if (serialBelongsToPatientGroup) {
                    tempDevice.serialNumber = saved.serialNumber;
                    tempDevice.serialNumberId = saved.serialNumberId;
                }
            }
            dispatch(setDevice(tempDevice));
            // Cached patient modality values are Perin Patch–only; don't pre-fill third-party
            // device thresholds from Patch-cached values when modalityIds overlap.
            const isPerinPatch = device.deviceId === 1;
            device.modalities.forEach((m) => {
                let tempModality = { 
                    ...modalityInfo, 
                    deviceId: device.deviceId, 
                    deviceModalityId: m.deviceModalityId,
                    modalityName: m.modalityName,
                    modalityId: m.modalityId,
                    modalityType: m.modalityType,
                    modalityTypeDisplayName: m.modalityTypeDisplayName    
                };
                if (isPerinPatch && savedModalityValues.hasOwnProperty(m.modalityId)) {
                    tempModality.upperLimit = savedModalityValues[m.modalityId].upperLimit;
                    tempModality.lowerLimit = savedModalityValues[m.modalityId].lowerLimit;
                    tempModality.targetValue = savedModalityValues[m.modalityId].targetValue;
                }
                dispatch(setModality(tempModality));
            });
        }
    };

    const removeDeviceCard = (id) => {
        setSelectedDevices(selectedDevices.filter(item => item.deviceId !== id));
        dispatch(removeDevice(id));
        dispatch(removeModality(id));
    };

    const handleNotes = (e) => {
        setCount(e.target.value.length);
        dispatch(setFormValue({ key: "deviceNotes", value: e.target.value }));
    };

    useEffect(() => {
        if (hubs?.length > 0 && hubs[0]?.serialNumber) {
            setLocalHubSerialNumber(hubs[0].serialNumber);
            setShowHubSerialInput(true);
        } else {
            setLocalHubSerialNumber('');
            setShowHubSerialInput(false);
        }
    }, [hubs]);    

    const handleToggleSerialInput = () => {
        setShowHubSerialInput(!showHubSerialInput);
    };

    const handleHubSerialNumberChange = (e) => {
        const newSerialNumber = e.target.value;
        setLocalHubSerialNumber(newSerialNumber);
        dispatch(setHubSerialNumber({ index: 0, serialNumber: newSerialNumber }));
    };

    const handleSetSerialNumber = ({id,serial, serialId}) => {
        dispatch(setSerialNumber({id, serial, serialId}))
    };
    
    const hubInfoTooltip = (
        <Tooltip id="hub-info-tooltip">
            The Hub is an enhanced patient monitoring device that integrates with wearable devices to ensure seamless data transfer.
        </Tooltip>
    );

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px", paddingBottom:"10px"  }}>
            <MultiSelectDropdown
                options={filteredDevices}
                label='listLabel'
                checkedKey='deviceId'
                checkSelectedItem={checkSelectedItem}
                handleChange={handleDeviceChange}
                selectedItems={selectedDevices}
                name='Devices'
                disabled={reenrolling || viewing}
            />
            <div className={styles.DropdownContainer} style={{ display: 'none' }}>
                <div className={showHubSerialInput ? styles.CardTitleOpened : styles.CardTitleClosed} style={{ display: "flex", alignItems: "center" }}>
                    <Form style={{ marginRight: "10px", display: 'none' }}>
                        <Form.Check
                            type="switch"
                            id="hubSerialToggle"
                            checked={showHubSerialInput}
                            onChange={handleToggleSerialInput}
                        />
                    </Form>
                    <span style={{
                        color: "#3F4346",
                        fontFamily: "Roboto",
                        fontSize: "20px",
                        fontStyle: "normal",
                        fontWeight: "400",
                        lineHeight: "28px",
                        display: 'none'
                    }} className={styles.CardTitleLabel}>Hub</span>
                    <OverlayTrigger placement="right" overlay={hubInfoTooltip}>
                        <FontAwesomeIcon icon="fa-solid fa-circle-info" style={{ marginLeft: "10px", color: '#686869', display: 'none' }} />
                    </OverlayTrigger>
                    {showHubSerialInput && (
                        <input
                            type="text"
                            placeholder="Serial Number"
                            className={inputStyles.InputStyling}
                            value={localHubSerialNumber}
                            onChange={handleHubSerialNumberChange}
                            style={{ width: "158px", height: "38px", marginLeft: "10px" }}
                        />
                    )}
                </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {selectedDevices.map((device, index) => (
                    <div key={device.deviceId}>
                        <ManagementDropdownCard
                            open={index === 0}
                            cardInfo={{ title: device.model, id: device.deviceId, type:"Device", modalities: device.modalities }}
                            removeHandler={removeDeviceCard}
                            updateHandler={setDevice}
                            dataList={enrollmentDevices}
                            patientEnrollment={true}
                            setModality={setModality}
                            errorShowing={errors}
                            disabled={reenrolling || viewing}
                            modalityList={programModalities}
                            allDevices={allDevices}
                            setSerialNumber={handleSetSerialNumber}
                        />
                    </div>
                ))}
                {selectedDevices.length >= 1 &&
                    <div style={{ position: "relative", display: "flex", flexDirection: "column", width: "300px", paddingTop: "20px" }}>
                        <span className={styles.FloatingLabel}>Notes</span>
                        <textarea className={styles.NoteInput}
                            id="description"
                            type="text"
                            maxLength={250}
                            disabled={viewing || reenrolling}
                            defaultValue={deviceNotes}
                            onChange={e => handleNotes(e)}
                        >
                        </textarea>
                        <span style={{ fontSize: "10px", color: "#929395", marginLeft: "auto" }}>
                            {count}/250
                        </span>
                    </div>
                }
            </div>
        </div>
    )
}

export default DeviceEnrollment;
